import { useEffect, useRef, useState } from "react";
import { ingredientData } from "@/mocks/landingData";

export default function IngredientsSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="ingredients" ref={ref} className="py-16 md:py-24 bg-background-100">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-8 lg:px-12">
        <div
          className={`text-center mb-12 md:mb-16 transition-all duration-600 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
          }`}
          style={{ transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)" }}
        >
          <p className="text-accent-500 text-base font-medium tracking-[0.2em] uppercase mb-3">
            Inside the Capsule
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground-950 leading-tight mb-4">
            Five Studied Ingredients
          </h2>
          <p className="text-foreground-700 text-xl max-w-2xl mx-auto">
            Each ingredient was selected for its specific role in supporting
            joint comfort, lubrication, and mobility.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {ingredientData.map((ing, idx) => (
            <div
              key={ing.id}
              className={`rounded-xl border border-background-200/70 bg-background-50 overflow-hidden card-hover-lift transition-all duration-600 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
              }`}
              style={{
                transitionDelay: `${idx * 80}ms`,
                transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)",
              }}
            >
              <div className="h-48 md:h-56 bg-background-100 overflow-hidden">
                <img
                  src={ing.image}
                  alt={ing.name}
                  className="w-full h-full object-cover hover-img-zoom"
                  width={400}
                  height={400}
                />
              </div>
              <div className="p-5 md:p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-base font-medium text-accent-500 uppercase tracking-wider">
                    Ingredient
                  </span>
                  <span className="text-base font-bold text-accent-500">
                    #{idx + 1}
                  </span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-1">
                  {ing.name}
                </h3>
                <p className="text-foreground-500 text-base mb-3">{ing.subtitle}</p>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <i className="ri-check-line text-sm" />
                  </div>
                  <span className="text-foreground-800 text-lg font-medium">
                    {ing.benefit}
                  </span>
                </div>
                <p className="text-foreground-600 text-lg leading-relaxed">
                  {ing.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}